import { useContext, useState, useEffect, useRef } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import './nav.css';
import { AuthContext } from '../_store';
import { IconLogo } from './icons';
import { apiFetch } from 'api/client';

export { Nav };

// Nav items config
const NAV_ITEMS = [
    { to: '/',           icon: 'dashboard',  label: 'Dashboard'  },
    { to: '/shaping',    icon: 'speed',      label: 'Shaping'    },
    { to: '/lists',      icon: 'list_alt',   label: 'IP Lists'   },
    { to: '/ddos-guard', icon: 'shield',     label: 'DDoS Guard' },
    { to: '/logs',       icon: 'manage_search', label: 'Logs'     },
    { to: '/backups',    icon: 'settings_backup_restore', label: 'Backups' },
];

const BOTTOM_ITEMS = [
    { to: '/profile', icon: 'manage_accounts', label: 'Profile'  },
    { to: '/organization-settings', icon: 'apartment', label: 'Organization' },
];

const persistActiveOrganization = async (orgId) => {
    const res = await apiFetch('/auth/set-active-organization', {
        method: 'POST',
        json: { organization_id: orgId },
    });
    if (!res.ok) {
        throw new Error(`Failed to set active organization (${res.status})`);
    }
};

function Nav({ authUser, collapsed, onToggle }) {
    const location = useLocation();
    const { userRole, selectedOrganization, setSelectedOrganization, orgPreferences, logout } = useContext(AuthContext);
    const [organizations, setOrganizations] = useState([]);
    const [loading] = useState(false);
    const [switchingOrganization, setSwitchingOrganization] = useState(false);
    const [loggingOut, setLoggingOut] = useState(false);
    const [logoutError, setLogoutError] = useState('');
    const initializingOrganizationRef = useRef(null);

    // Fetch organizations when component mounts
    useEffect(() => {
        if (authUser) {
            const orgs = authUser.organizations || [];
            setOrganizations(orgs);
            if (orgs.length > 0 && !selectedOrganization) {
                const defaultOrg = authUser.default_org_id || orgs[0].id || orgs[0].name;
                if (initializingOrganizationRef.current !== String(defaultOrg)) {
                    initializingOrganizationRef.current = String(defaultOrg);
                    setSwitchingOrganization(true);
                    persistActiveOrganization(defaultOrg)
                        .then(() => setSelectedOrganization(String(defaultOrg)))
                        .catch((err) => console.error('Error setting initial organization:', err))
                        .finally(() => {
                            initializingOrganizationRef.current = null;
                            setSwitchingOrganization(false);
                        });
                }
            }
        }
    }, [authUser, selectedOrganization, setSelectedOrganization]);

    const handleOrganizationChange = async (event) => {
        const nextOrganization = event.target.value;
        if (!nextOrganization || nextOrganization === String(selectedOrganization)) return;

        setSwitchingOrganization(true);
        try {
            // Persist first so organization-dependent consumers (especially the
            // Grafana iframe) cannot race ahead with the previous server scope.
            await persistActiveOrganization(nextOrganization);
            setSelectedOrganization(nextOrganization);
        } catch (err) {
            console.error('Error changing active organization:', err);
        } finally {
            setSwitchingOrganization(false);
        }
    };

    const handleLogout = async () => {
        setLoggingOut(true);
        setLogoutError('');
        try {
            await logout();
        } catch (error) {
            console.error('Logout request failed:', error);
            setLogoutError('Logout failed. Please try again.');
            setLoggingOut(false);
        }
    };

    if (!authUser) return null;

    return (
        <aside className={`sidebar-root${collapsed ? ' collapsed' : ''}`}>
                {/* Brand row: logo + collapse button */}
                <div className="sidebar-brand">
                    <NavLink to="/" aria-label="Home" className="sidebar-brand-logo-link">
                        <IconLogo
                            height={44}
                            brandColor="var(--color-text-strong)"
                            accentColor="var(--color-accent-teal)"
                            accentOpacity={0.8}
                        />
                    </NavLink>
                    <button
                        type="button"
                        className="sidebar-toggle-inline"
                        onClick={onToggle}
                        aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
                    >
                        <span className="material-symbols-outlined">{collapsed ? 'menu' : 'menu_open'}</span>
                    </button>
                </div>

            {/* Org info */}
            <div className="sidebar-org">
                <div className="sidebar-org-label">Organization</div>
                <div className="sidebar-org-name">
                    {loading ? 'Loading…' : organizations.length === 0 ? 'No organizations' : (
                        <select
                            value={selectedOrganization}
                            onChange={handleOrganizationChange}
                            disabled={loading || switchingOrganization || organizations.length <= 1}
                            className="sidebar-org-select"
                        >
                            {organizations.map((org) => (
                                <option key={org.id || org.name} value={org.id ?? org.name}>
                                    {org.name || org.displayName || org.id}
                                </option>
                            ))}
                        </select>
                    )}
                </div>
                {/* <div className="sidebar-org-version">NetX OS</div> */}
            </div>

            {/* Main nav */}
            <nav className="sidebar-nav">
                {NAV_ITEMS.map(({ to, icon, label }) => {
                    // Exact match for home, prefix match for others
                    const isActive = to === '/'
                        ? location.pathname === '/'
                        : location.pathname.startsWith(to);
                    return (
                        <NavLink
                            key={to}
                            to={to}
                            className={`sidebar-nav-item${isActive ? ' active' : ''}`}
                            data-label={label}
                        >
                            <span className="material-symbols-outlined sidebar-nav-icon">{icon}</span>
                            <span className="sidebar-nav-label">{label}</span>
                        </NavLink>
                    );
                })}

                {userRole === 'admin' && (
                    <NavLink
                        to="/admin"
                        className={({ isActive }) => `sidebar-nav-item${isActive ? ' active' : ''}`}
                        data-label="Admin"
                    >
                        <span className="material-symbols-outlined sidebar-nav-icon">admin_panel_settings</span>
                        <span className="sidebar-nav-label">Admin</span>
                    </NavLink>
                )}

                {orgPreferences?.flowx_enabled && (
                    <a
                        href={orgPreferences.flowx_url || 'https://flowx.netx.as'}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="sidebar-nav-item"
                        data-label="FlowX"
                    >
                        <span className="material-symbols-outlined sidebar-nav-icon">monitoring</span>
                        <span className="sidebar-nav-label">FlowX</span>
                        <span className="material-symbols-outlined sidebar-nav-external">open_in_new</span>
                    </a>
                )}
            </nav>

            {/* Bottom actions */}
            <div className="sidebar-bottom">
                {BOTTOM_ITEMS.map(({ to, icon, label }) => (
                    <NavLink
                        key={to}
                        to={to}
                        className={({ isActive }) => `sidebar-bottom-item${isActive ? ' active' : ''}`}
                        data-label={label}
                    >
                        <span className="material-symbols-outlined sidebar-nav-icon">{icon}</span>
                        <span className="sidebar-nav-label">{label}</span>
                    </NavLink>
                ))}
                <button
                    type="button"
                    onClick={handleLogout}
                    disabled={loggingOut}
                    className="sidebar-bottom-item"
                    data-label={loggingOut ? 'Logging out…' : 'Logout'}
                >
                    <span className="material-symbols-outlined sidebar-nav-icon">logout</span>
                    <span className="sidebar-nav-label">{loggingOut ? 'Logging out…' : 'Logout'}</span>
                </button>
                {logoutError && <div className="sidebar-logout-error" role="alert">{logoutError}</div>}
            </div>
        </aside>
    );
}
